import { useState } from 'react';
import { Card, GroupedBarChart, MaterialSymbol, PaasTransaction, Select, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import type { EnrichedReports } from '#shared/hooks/useApi/useFetchEnrichedReports';
import type { EnrichmentTier } from '../useDataEnrichment';
import MetricTooltip from '../MetricTooltip';
import useMetricTooltips from '../useMetricTooltips';
import PieCard from '../PieCard/PieCard';
import RiskMeterCard from '../RiskMeterCard/RiskMeterCard';
import CategoryDrawer from '../CategoryDrawer/CategoryDrawer';
import TransactionsDrawer from '../TransactionsDrawer/TransactionsDrawer';
import { formatAmount, formatSignedAmount } from '../formatAmount';
import {
  DateRangeDays,
  buildCategoryTotals,
  buildMonthlySeries,
  categoryTotalFor,
  countEnrichedTransactions,
  daysBetweenTransactions,
  formatMonthKey,
  getVisibleMonths,
  variabilityRating,
} from '../insightsModel';
import { CategoryDescriptor, categoriesOfKind } from '../categories';
import styles from './InsightsPanel.module.scss';

const cn = withStyles(styles);

const INCOME_BAR_COLOR = '#2F82FF';
// The light end of the ordinal ramp — still clears 2:1 against the surface
const EXPENSE_BAR_COLOR = '#7FB0FF';
const NET_GREEN = '#008107';

type InsightsPanelProps = {
  reports: EnrichedReports;
  transactions: PaasTransaction[];
  tier: EnrichmentTier;
  currency: string;
  categories: CategoryDescriptor[];
  onUpdateCategory: (transactionID: string, classification: string) => Promise<void>;
};

export default function InsightsPanel({
  reports,
  transactions,
  tier,
  currency,
  categories,
  onUpdateCategory,
}: InsightsPanelProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.Customers.PaymentMethodsTab.DataEnrichment.Insights',
  });

  const tooltips = useMetricTooltips();

  const [rangeDays, setRangeDays] = useState<DateRangeDays>(90);
  const [activeCategory, setActiveCategory] = useState<CategoryDescriptor | null>(null);
  const [activeTransactionsKind, setActiveTransactionsKind] = useState<'income' | 'expense' | null>(null);

  const isEnhanced = tier === 'enhanced';
  const months = getVisibleMonths(reports, rangeDays);

  const incomeCategories = categoriesOfKind(categories, 'income');
  const expenseCategories = categoriesOfKind(categories, 'expense');
  const nsfCategory = categories.find((category) => category.kind === 'fee' && category.side === 'credit');
  const bankFeeCategory = categories.find((category) => category.kind === 'fee' && category.side === 'debit');

  const incomeTotals = buildCategoryTotals(incomeCategories, reports, months);
  const expenseTotals = buildCategoryTotals(expenseCategories, reports, months);
  const incomeSeries = buildMonthlySeries(incomeCategories, reports, months);
  const expenseSeries = buildMonthlySeries(expenseCategories, reports, months);

  const totalIncome = incomeTotals.reduce((sum, item) => sum + item.amount, 0);
  const totalExpense = expenseTotals.reduce((sum, item) => sum + item.amount, 0);
  const netBalance = totalIncome - totalExpense;
  const numEnriched = countEnrichedTransactions(categories, reports, months);

  const nsfFeeTotal = nsfCategory ? categoryTotalFor(nsfCategory, reports, months) : 0;
  const bankFeeTotal = bankFeeCategory ? categoryTotalFor(bankFeeCategory, reports, months) : 0;

  const { riskReport, transactionReport } = reports;

  function resolveLabel(descriptor: CategoryDescriptor): string {
    return t(`Categories.${descriptor.labelKey}`, { defaultValue: descriptor.defaultLabel });
  }

  // DaysWith* report maps are keyed 'YYYY-MM' with a day-of-month value
  function formatDayMap(dayMap: Record<string, string | null> | undefined): string {
    if (!dayMap) return '—';

    const entries = months
      .map((monthKey) => {
        const day = dayMap[monthKey];

        return day ? `${formatMonthKey(monthKey, { withYear: true }).replace(' ', ` ${Number(day)}, `)}` : null;
      })
      .filter((entry): entry is string => entry !== null);

    return entries.length > 0 ? entries.join('\n') : '—';
  }

  const rangeOptions = [
    { value: '90', label: t('last90Days', { defaultValue: 'Last 90 days' }) },
    { value: '60', label: t('last60Days', { defaultValue: 'Last 60 days' }) },
    { value: '30', label: t('last30Days', { defaultValue: 'Last 30 days' }) },
  ];

  const variabilityLabels = {
    excellent: t('ratingExcellent', { defaultValue: 'Excellent' }),
    good: t('ratingGood', { defaultValue: 'Good' }),
    fair: t('ratingFair', { defaultValue: 'Fair' }),
    inconsistent: t('ratingInconsistent', { defaultValue: 'Inconsistent' }),
  };

  return (
    <div className={cn('container')} data-testid="insights-panel">
      <div className={cn('headerRow')}>
        <div className={cn('enrichedBanner')}>
          <MaterialSymbol icon="check_circle" size="18px" color={NET_GREEN} />

          {t('transactionsEnriched', {
            defaultValue: '{{numEnriched}} Transactions Enriched',
            numEnriched,
          })}
        </div>

        <div className={cn('rangeFilter')}>
          <Select
            version="2"
            name="insightsDateRange"
            options={rangeOptions}
            value={String(rangeDays)}
            onChange={(_event, { value }) => {
              const rangeByValue: Record<string, DateRangeDays> = { '90': 90, '60': 60, '30': 30 };
              const nextRange = rangeByValue[value];
              if (nextRange) setRangeDays(nextRange);
            }}
          />
        </div>
      </div>

      <Card className={cn('chartCard')}>
        <div className={cn('chartHeader')}>
          {isEnhanced ? (
            <div className={cn('chartHeaderStats')}>
              <div className={cn('chartHeaderStat')}>
                <span className={cn('chartStatLabel')}>{t('netBalance', { defaultValue: 'Net Balance' })}</span>
                <span className={cn('chartStatValue', 'net')}>{formatSignedAmount(netBalance, currency)}</span>
              </div>

              <div className={cn('chartHeaderStat')}>
                <span className={cn('chartStatLabel')}>{t('avgBalance', { defaultValue: 'Avg. Balance' })}</span>

                <span className={cn('chartStatValue')}>
                  {riskReport ? formatAmount(riskReport.AverageBalance, currency) : '—'}
                </span>
              </div>

              <div className={cn('chartHeaderStat')}>
                <span className={cn('chartStatLabel')}>
                  {t('avgMonthEndBalance', { defaultValue: 'Avg. Month-End Balance' })}
                </span>

                <span className={cn('chartStatValue')}>
                  {transactionReport ? formatAmount(transactionReport.AverageMonthEndBalance, currency) : '—'}
                </span>
              </div>
            </div>
          ) : (
            <div className={cn('chartHeaderStat')}>
              <span className={cn('chartStatLabel')}>{t('netBalance', { defaultValue: 'Net Balance' })}</span>
              <span className={cn('chartStatValue', 'net')}>{formatSignedAmount(netBalance, currency)}</span>
            </div>
          )}

          <div className={cn('chartLegend')}>
            <span className={cn('chartLegendItem')}>
              <span className={cn('legendDot')} style={{ background: INCOME_BAR_COLOR }} />
              {t('totalIncome', { defaultValue: 'Total Income' })}
            </span>

            <span className={cn('chartLegendItem')}>
              <span className={cn('legendDot')} style={{ background: EXPENSE_BAR_COLOR }} />
              {t('totalExpense', { defaultValue: 'Total Expense' })}
            </span>
          </div>
        </div>

        <GroupedBarChart
          labels={months.map((monthKey) => formatMonthKey(monthKey))}
          series={[
            {
              label: t('totalIncome', { defaultValue: 'Total Income' }),
              values: incomeSeries,
              color: INCOME_BAR_COLOR,
            },
            {
              label: t('totalExpense', { defaultValue: 'Total Expense' }),
              values: expenseSeries,
              color: EXPENSE_BAR_COLOR,
            },
          ]}
          formatValue={(value) => formatAmount(value, currency)}
          getTooltipFooter={(index) => {
            const income = incomeSeries[index] ?? 0;
            const expense = expenseSeries[index] ?? 0;

            const lines = [
              t('tooltipNet', {
                defaultValue: 'Net: {{net}}',
                net: formatSignedAmount(income - expense, currency),
              }),
            ];

            if (income > 0) {
              lines.push(
                t('tooltipPctOfIncome', {
                  defaultValue: '{{pct}}% of Income',
                  pct: Math.round((expense / income) * 100),
                }),
              );
            }

            return lines;
          }}
        />

        {isEnhanced && riskReport && (
          <div className={cn('statCardRow')}>
            <div className={cn('statCard')}>
              <span className={cn('statCardLabel')}>
                {t('creditDebitRatio', { defaultValue: 'Credit / Debit Ratio' })}
                <MetricTooltip information={tooltips.creditDebitRatio} />
              </span>

              <span className={cn('statCardValue')}>{riskReport.IncomeExpenseRatio.toFixed(2)}x</span>
            </div>

            <div className={cn('statCard')}>
              <span className={cn('statCardLabel')}>
                {t('totalTransactions', { defaultValue: 'Total Transactions' })}
              </span>

              <span className={cn('statCardValue')}>{riskReport.TotalTransactions}</span>
            </div>

            <div className={cn('statCard')}>
              <span className={cn('statCardLabel')}>
                {t('transactionFrequency', { defaultValue: 'Transaction Frequency' })}
                <MetricTooltip information={tooltips.transactionFrequency} />
              </span>

              <span className={cn('statCardValue')}>
                {(() => {
                  const days = daysBetweenTransactions(riskReport.TransactionFrequency);

                  return days !== null
                    ? t('frequencyDays', { defaultValue: '{{days}} Days', days: days.toFixed(1) })
                    : '—';
                })()}
              </span>
            </div>

            <div className={cn('statCard')}>
              <span className={cn('statCardLabel')}>
                {t('largeTransactions', { defaultValue: 'Large Transactions' })}
                <MetricTooltip information={tooltips.largeTransactions} />
              </span>

              <span className={cn('statCardValue')}>{riskReport.LargeTransactions}</span>
            </div>
          </div>
        )}
      </Card>

      <div className={cn('pieRow')}>
        <PieCard
          title={t('totalIncome', { defaultValue: 'Total Income' })}
          currency={currency}
          totals={incomeTotals}
          resolveLabel={resolveLabel}
          onSliceClick={setActiveCategory}
          onViewTransactions={() => setActiveTransactionsKind('income')}
          viewTransactionsLabel={t('viewIncomeTransactions', { defaultValue: 'View Income Transactions' })}
        >
          {isEnhanced && transactionReport && riskReport && (
            <div className={cn('analyticsCard')}>
              <AnalyticsRow
                label={t('avgCreditAmount', { defaultValue: 'Avg. Credit Amount' })}
                tooltip={tooltips.avgCreditAmount}
                value={formatAmount(transactionReport.AverageCreditAmount, currency)}
              />

              <AnalyticsRow
                label={t('avgCreditsPerMonth', { defaultValue: 'Avg. Credits/Month' })}
                tooltip={tooltips.avgCreditsPerMonth}
                value={transactionReport.AverageCreditsPerMonth}
              />

              <AnalyticsRow
                label={t('percentile90Credit', { defaultValue: '90th Percentile Credit' })}
                tooltip={tooltips.percentile90Credit}
                value={formatAmount(transactionReport['90thPercentileCreditAmount'], currency)}
              />

              <AnalyticsRow
                label={t('incomeStability', { defaultValue: 'Income Stability' })}
                tooltip={tooltips.incomeStability}
                value={`± ${formatAmount(riskReport.IncomeStability, currency)}`}
              />

              <AnalyticsRow
                label={t('incomeVariability', { defaultValue: 'Income Variability' })}
                tooltip={tooltips.incomeVariability}
                value={`${riskReport.IncomeCoefficientOfVariation.toFixed(1)}% — ${
                  variabilityLabels[variabilityRating(riskReport.IncomeCoefficientOfVariation)]
                }`}
              />

              <AnalyticsRow
                label={t('daysWithMostCredits', { defaultValue: 'Day(s) with most credits' })}
                value={formatDayMap(transactionReport.DaysWithMostCredits)}
              />

              <AnalyticsRow
                label={t('dayWithHighestBalance', { defaultValue: 'Day with Highest Balance' })}
                value={formatDayMap(transactionReport.DaysWithHighestBalance)}
              />
            </div>
          )}
        </PieCard>

        <PieCard
          title={t('totalExpense', { defaultValue: 'Total Expense' })}
          currency={currency}
          totals={expenseTotals}
          resolveLabel={resolveLabel}
          onSliceClick={setActiveCategory}
          onViewTransactions={() => setActiveTransactionsKind('expense')}
          viewTransactionsLabel={t('viewExpenseTransactions', { defaultValue: 'View Expense Transactions' })}
        >
          {isEnhanced && transactionReport && riskReport && (
            <div className={cn('analyticsCard')}>
              <AnalyticsRow
                label={t('avgDebitAmount', { defaultValue: 'Avg. Debit Amount' })}
                value={formatAmount(transactionReport.AverageDebitAmount, currency)}
              />

              <AnalyticsRow
                label={t('avgDebitsPerMonth', { defaultValue: 'Avg. Debits/Month' })}
                value={transactionReport.AverageDebitsPerMonth}
              />

              <AnalyticsRow
                label={t('percentile90Debit', { defaultValue: '90th Percentile Debit' })}
                tooltip={tooltips.percentile90Debit}
                value={formatAmount(transactionReport['90thPercentileDebitAmount'], currency)}
              />

              <AnalyticsRow
                label={t('expenseVolatility', { defaultValue: 'Expense Volatility' })}
                value={`± ${formatAmount(riskReport.SpendingVolatility, currency)}`}
              />

              <AnalyticsRow
                label={t('expenseVolatilityPct', { defaultValue: 'Expense Volatility %' })}
                value={`${riskReport.SpendingCoefficientOfVariation.toFixed(1)}% — ${
                  variabilityLabels[variabilityRating(riskReport.SpendingCoefficientOfVariation)]
                }`}
              />

              <AnalyticsRow
                label={t('daysWithMostDebits', { defaultValue: 'Day(s) with most debits' })}
                value={formatDayMap(transactionReport.DaysWithMostDebits)}
              />

              <AnalyticsRow
                label={t('dayWithLowestBalance', { defaultValue: 'Day with Lowest Balance' })}
                value={formatDayMap(transactionReport.DaysWithLowestBalance)}
              />
            </div>
          )}
        </PieCard>
      </div>

      {isEnhanced && riskReport ? (
        <>
          {/* div-with-button-role (not a <button>) because the tooltip inside renders its own button */}
          <div
            role="button"
            tabIndex={0}
            className={cn('feeCard', 'bankFeeCard')}
            onClick={() => bankFeeCategory && setActiveCategory(bankFeeCategory)}
            onKeyDown={(event) => {
              if ((event.key === 'Enter' || event.key === ' ') && bankFeeCategory) setActiveCategory(bankFeeCategory);
            }}
          >
            <span className={cn('bankFeeInfo')}>
              <span className={cn('bankFeeTitle')}>
                {t('bankFee', { defaultValue: 'Bank Fee' })}
                <MetricTooltip information={tooltips.bankFee} />
              </span>

              <span className={cn('bankFeeSub')}>
                {t('bankFeeSub', { defaultValue: 'Total non-NSF bank fees in the selected period' })}
              </span>
            </span>

            <span className={cn('feeValue')}>{formatAmount(bankFeeTotal, currency)}</span>
          </div>

          <RiskMeterCard
            riskReport={riskReport}
            transactionReport={transactionReport}
            nsfFeeTotal={nsfFeeTotal}
            currency={currency}
            onClick={() => nsfCategory && setActiveCategory(nsfCategory)}
          />
        </>
      ) : (
        <div className={cn('feeRow')}>
          <button type="button" className={cn('feeCard')} onClick={() => nsfCategory && setActiveCategory(nsfCategory)}>
            <span className={cn('feeValue')}>{formatAmount(nsfFeeTotal, currency)}</span>
            <span className={cn('feeLabel')}>{t('nsfFee', { defaultValue: 'NSF Fee' })}</span>
          </button>

          <button
            type="button"
            className={cn('feeCard')}
            onClick={() => bankFeeCategory && setActiveCategory(bankFeeCategory)}
          >
            <span className={cn('feeValue')}>{formatAmount(bankFeeTotal, currency)}</span>
            <span className={cn('feeLabel')}>{t('bankFee', { defaultValue: 'Bank Fee' })}</span>
          </button>
        </div>
      )}

      <CategoryDrawer
        descriptor={activeCategory}
        categoryLabel={activeCategory ? resolveLabel(activeCategory) : ''}
        reports={reports}
        transactions={transactions}
        months={months}
        currency={currency}
        isEnhanced={isEnhanced}
        categories={categories}
        onClose={() => setActiveCategory(null)}
        onUpdateCategory={onUpdateCategory}
      />

      <TransactionsDrawer
        kind={activeTransactionsKind}
        descriptors={activeTransactionsKind === 'income' ? incomeCategories : expenseCategories}
        categories={categories}
        transactions={transactions}
        months={months}
        currency={currency}
        onClose={() => setActiveTransactionsKind(null)}
        onUpdateCategory={onUpdateCategory}
      />
    </div>
  );
}

type AnalyticsRowProps = {
  label: string;
  value: React.ReactNode;
  tooltip?: string;
};

function AnalyticsRow({ label, value, tooltip }: AnalyticsRowProps) {
  return (
    <div className={cn('analyticsRow')}>
      <span className={cn('analyticsLabel')}>
        {label}
        {tooltip && <MetricTooltip information={tooltip} />}
      </span>

      <span className={cn('analyticsValue')}>{value}</span>
    </div>
  );
}
